import api from './api';

/** One vehicle in the broker's own book, keyed by its registration number. */
export interface VehicleDatabaseRow {
  registration_number: string;
  make_name: string;
  model_name: string;
  variant_name: string;
  fuel_type: string;
  manufacturing_year: number | null;
  engine_number: string;
  chassis_number: string;
  registration_date: string | null;
  customer_id: string | null;
  customer_name: string;
  /** Where this row came from. 'registry' is the COMMON trail — a
   *  vendor-sourced record shared by every broker, which this broker may have
   *  nothing to do with. */
  source: 'policy' | 'customer' | 'registry';
  /** Whether the broker has a policy or a customer record on this vehicle.
   *  False for a vehicle they can see only because the trail is common. */
  in_your_book: boolean;
}

/** One of our own policies on this vehicle. */
export interface VehicleDatabasePolicy {
  id: string;
  policy_number: string;
  /** B-05: the insurer's number; blank when they issued none. */
  insurer_policy_number?: string;
  insurer_name: string;
  /** U-05: the insurer's id, logo ('' when none) and blurb ('' when none). */
  insurer_id?: string | null;
  insurer_logo_url?: string;
  insurer_description?: string;
  status: string;
  inception_date: string | null;
  expiry_date: string | null;
  total_premium: string;
}

/** An observed change of owner, from a bought registration lookup. */
export interface VehicleOwnershipObservation {
  owner_name: string;
  owner_serial_number: string;
  observed_at: string;
}

/** An observed policy on this vehicle, from a bought registration lookup.
 *
 *  There is deliberately no broker field: whether the cover was OURS is useful
 *  (`placed_by_you`), but which competitor placed it is not something a broker
 *  bought when they bought a lookup. */
export interface VehicleInsuranceObservation {
  /** What the vendor wrote -- kept verbatim, even when it matched an insurer. */
  insurer_name: string;
  /** U-05: the MATCHED insurer's logo and blurb; '' when the vendor's text
   *  matched none of ours, which draws no logo at all rather than a guess. */
  insurer_logo_url?: string;
  insurer_description?: string;
  policy_number: string;
  valid_from: string | null;
  valid_until: string | null;
  placed_by_you: boolean;
  observed_at: string;
}

export interface VehicleDatabaseDetail extends VehicleDatabaseRow {
  make_id: string | null;
  model_id: string | null;
  variant_id: string | null;
  our_policies: VehicleDatabasePolicy[];
  ownership_history: VehicleOwnershipObservation[];
  insurance_history: VehicleInsuranceObservation[];
  /** Whether a vendor lookup has ever been bought for this vehicle. When
   *  false, the two history lists are empty because nothing was ever bought —
   *  not because the vehicle has no history. */
  in_registry: boolean;
  registered_owner?: string;
}

/** B10: `?ordering=` vocabulary the backend actually understands
 *  (`VehicleDatabaseView.ORDERINGS`, `backend/apps/vehicles/views.py`) --
 *  Registered Date only, ascending or descending. */
export type VehicleDatabaseOrdering = 'registration_date' | '-registration_date';

export interface VehicleDatabaseListParams {
  search?: string;
  offset?: number;
  /** B10: the "Yours" badge as a filter -- narrows to vehicles this broker
   *  actually holds (a policy or a customer record), same membership rule
   *  the badge itself already used to compute `in_your_book`. */
  mine?: boolean;
  ordering?: VehicleDatabaseOrdering;
}

export const vehicleDatabaseService = {
  async list(
    params: VehicleDatabaseListParams = {},
  ): Promise<{ count: number; results: VehicleDatabaseRow[]; next_offset: number | null }> {
    const { search, offset = 0, mine, ordering } = params;
    const response = await api.get<{
      count: number;
      results: VehicleDatabaseRow[];
      next_offset: number | null;
    }>('/vehicles/database/', {
      params: {
        ...(search ? { search } : {}),
        ...(offset ? { offset } : {}),
        ...(mine ? { mine: 'true' } : {}),
        ...(ordering ? { ordering } : {}),
      },
    });
    return response.data;
  },

  async get(registrationNumber: string): Promise<VehicleDatabaseDetail> {
    const response = await api.get<VehicleDatabaseDetail>(
      `/vehicles/database/${encodeURIComponent(registrationNumber)}/`,
    );
    return response.data;
  },
};
